import moment from 'moment';
import {
  Inviter,
  SessionState,
  UserAgent,
  Registerer,
  Web,
  TransportState,
  RegistererState,
  type UserAgentOptions,
  type InviterInviteOptions,
  type InviterOptions,
  type Invitation,
  type InvitationAcceptOptions,
} from 'sip.js';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import { ref, type Ref } from 'vue';

import phoneCall from '@bv/assets/audio/call.mp3';
import {
  dtmf0,
  dtmf1,
  dtmf2,
  dtmf3,
  dtmf4,
  dtmf5,
  dtmf6,
  dtmf7,
  dtmf8,
  dtmf9,
  dtmfStar,
  dtmfHash,
} from '@bv/assets/audio/dtmf';
import ringtone from '@bv/assets/audio/ringing.mp3';
import circle from '@bv/assets/img/icons/circle.svg';
import { useCrypto, useLocalStorage, useToast, useUserIpDetails } from '@bv/composables/common';
import { Call, useSipStore, useUserStore } from '@bv/stores';
import { notification } from '@bv/utils/common';
import { getLocation } from '@bv/utils/common/get-location';
import { trans } from '@bv/utils/i18n';
import normalize from '@bv/utils/voip/normalize';

const workspacesRequiringSaLocation: string[] = ['DemoTrial-dev', 'DemoTrial-stg', 'DemoTrial'];

const SIP_OFFLINE_KEY = 'bv_sip_offline';

/**
 * @typedef {`${TransportState}`} ConnectionStatus
 * A string literal type representing the SIP transport connection state.
 */
export type ConnectionStatus = `${TransportState}`;

/**
 * @typedef {`${RegistererState}`} RegistrationStatus
 * A string literal type representing the SIP registration state.
 */
export type RegistrationStatus = `${RegistererState}`;

/**
 * Helper function to get location header value from geolocation.
 * @returns {Promise<string | undefined>} Location string in format "lat,lon" or undefined if unavailable.
 */
async function getLocationHeader(): Promise<string | undefined> {
  try {
    const location = await getLocation();
    if (location?.coords) {
      return `geo:${location.coords.latitude},${location.coords.longitude};`;
    }
  } catch {
    // Location unavailable, return undefined
  }
  return undefined;
}

/**
 * Helper function to build extra headers array with x-location and test header.
 * Note: User-Agent is set globally via userAgentString in UserAgentOptions.
 * @param {string | undefined} location - Optional location string.
 * @returns {string[]} Array of header strings.
 */
function buildExtraHeaders(location?: string): string[] {
  const headers: string[] = [];
  if (location) {
    headers.push(`X-Location: ${location}`);
  }
  return headers;
}

/**
 * Default options for accepting an incoming call (invitation),
 * specifying audio-only constraints.
 */
const invitationAcceptOptions: InvitationAcceptOptions = {
  sessionDescriptionHandlerOptions: {
    constraints: {
      audio: true,
      video: false,
    },
  },
};

/**
 * A record mapping DTMF characters to their corresponding audio file paths for local playback.
 * @type {Record<string, string>}
 */
const tones: Record<string, string> = {
  '0': dtmf0,
  '1': dtmf1,
  '2': dtmf2,
  '3': dtmf3,
  '4': dtmf4,
  '5': dtmf5,
  '6': dtmf6,
  '7': dtmf7,
  '8': dtmf8,
  '9': dtmf9,
  '*': dtmfStar,
  '#': dtmfHash,
};

/**
 * A singleton class to manage the entire lifecycle of a SIP (WebRTC) phone.
 * Handles connection, registration, calls, and media.
 */
class Sip {
  /** @private Local storage helper for SIP persistence flags. */
  private storage = useLocalStorage();

  /** @private The user's SIP extension number. */
  private extension?: string;

  /** @private The SIP server URL (domain part). */
  private url?: string;

  /** @private The full WebSocket server URI for SIP transport. */
  private server?: string;

  /** @private The sip.js UserAgent instance. */
  private userAgent?: UserAgent;

  /** @private The sip.js Registerer instance. */
  private registerer?: Registerer;

  /** @private Cached location string for headers. */
  private cachedLocation?: string;

  /** @private Reactive reference to the current transport connection status. */
  private _connectionStatus: Ref<ConnectionStatus> = ref<ConnectionStatus>('Disconnected');

  /** @private Reactive reference to the current registration status. */
  private _registrationStatus: Ref<RegistrationStatus> = ref<RegistrationStatus>('Unregistered');

  /**
   * Public getter for the reactive connection status.
   * @returns {Ref<ConnectionStatus>}
   */
  public get connectionStatus(): Ref<ConnectionStatus> {
    return this._connectionStatus;
  }

  /**
   * Public getter for the reactive registration status.
   * @returns {Ref<RegistrationStatus>}
   */
  public get registrationStatus(): Ref<RegistrationStatus> {
    return this._registrationStatus;
  }

  /**
   * @private
   * Returns current service id as a normalized string.
   * @returns {string | undefined} Current service id or undefined.
   */
  private get currentServiceId(): string | undefined {
    const serviceId = useUserStore().currentService?.id;
    return serviceId !== undefined && serviceId !== null ? String(serviceId) : undefined;
  }

  /**
   * @private
   * Reads SIP offline service ids from storage with backward compatibility.
   * @returns {string[]} Offline service ids.
   */
  private get offlineServiceIds(): string[] {
    const storedValue = this.storage.get<string[]>(SIP_OFFLINE_KEY);

    if (Array.isArray(storedValue)) {
      return storedValue.filter((serviceId): serviceId is string => typeof serviceId === 'string');
    }

    return [];
  }

  /**
   * @private
   * Checks if current service id exists in offline list.
   * @returns {boolean} Whether current service is marked offline.
   */
  private get isCurrentServiceOffline(): boolean {
    const currentServiceId = this.currentServiceId;

    if (!currentServiceId) {
      return false;
    }

    return this.offlineServiceIds.includes(currentServiceId);
  }

  /**
   * @private
   * Ensures location is retrieved and cached for use in headers.
   * @returns {Promise<string | undefined>} Location string or undefined if unavailable.
   */
  private async ensureLocationCached(): Promise<string | undefined> {
    // Use cached location if available
    if (this.cachedLocation) {
      return this.cachedLocation;
    }

    // Try to get fresh location
    const location = await getLocationHeader();
    if (location) {
      this.cachedLocation = location;
    }
    return location;
  }

  /**
   * @private
   * Persists SIP offline service ids and removes key when no ids remain.
   * @param {string[]} serviceIds - Service ids to persist.
   */
  private setOfflineServiceIds(serviceIds: string[]): void {
    const uniqueServiceIds = [...new Set(serviceIds.filter((serviceId) => !!serviceId))];

    if (!uniqueServiceIds.length) {
      this.storage.remove(SIP_OFFLINE_KEY);
      return;
    }

    this.storage.set(SIP_OFFLINE_KEY, uniqueServiceIds);
  }

  /**
   * @private
   * Marks current service as SIP-offline.
   */
  private addCurrentServiceToOffline(): void {
    const currentServiceId = this.currentServiceId;

    if (!currentServiceId) {
      return;
    }

    this.setOfflineServiceIds([...this.offlineServiceIds, currentServiceId]);
  }

  /**
   * @private
   * Unmark current service from SIP-offline.
   */
  private removeCurrentServiceFromOffline(): void {
    const currentServiceId = this.currentServiceId;

    if (!currentServiceId) {
      return;
    }

    const nextOfflineServiceIds = this.offlineServiceIds.filter((serviceId) => serviceId !== currentServiceId);
    this.setOfflineServiceIds(nextOfflineServiceIds);
  }

  /**
   * @private
   * Creates, attaches, and plays an HTML <audio> element for the remote media stream.
   * @param {Call} call - The call instance to which the new audio element will be attached.
   * @param {MediaStream} stream - The remote media stream containing the audio to be played.
   * @returns {void}
   */
  private startRemoteAudioMedia(call: Call, stream: MediaStream): void {
    call.remoteAudioElement = document.createElement('audio');
    call.remoteAudioElement.srcObject = stream;
    call.remoteAudioElement.play();
  }

  /**
   * Establishes a connection to the SIP WebSocket server and initializes the UserAgent.
   * @returns {Promise<boolean>} A promise that resolves to true on successful connection, false otherwise.
   */
  public async connect(): Promise<boolean> {
    if (this.connectionStatus.value === 'Connected') {
      await this.register();
      return Promise.resolve(true);
    }

    const toast = useToast();

    const { useUserStore } = await import('@bv/stores');

    const sipOptions = useSipStore().options;

    if (!sipOptions || sipOptions.connection === false) {
      return Promise.resolve(false);
    }

    const sipPort: number | undefined = sipOptions.port;

    if (!sipPort) {
      return Promise.resolve(false);
    }

    let extensionDetails = useUserStore().extensionDetails;

    if (!extensionDetails) {
      await useUserStore().getService('call-center');
      extensionDetails = useUserStore().extensionDetails;

      if (!extensionDetails) {
        return Promise.resolve(false);
      }
    }

    if (extensionDetails.current_mode !== 'webPhone') {
      return Promise.resolve(false);
    }

    // Get location before connecting — abort if location is unavailable
    const location = await getLocationHeader();
    if (!location) {
      toast.error(trans('voip.not_allowed_call_reason.location_permission_required_trans'));
      return Promise.resolve(false);
    }
    this.cachedLocation = location;

    const { decrypt, result: password } = useCrypto();
    await decrypt(circle, extensionDetails.secret, 'hassanahmedseperator');

    this.extension = extensionDetails.number;
    this.url = extensionDetails.asterisk_server_pbx.replace('https://', '');
    this.server = `wss://${this.url}:${sipPort}/ws`;

    const aor: string = `sip:${this.extension}@${this.url}`;
    const uri = UserAgent.makeURI(aor);

    const transportOptions: Partial<UserAgentOptions> = {
      uri,
      authorizationUsername: this.extension,
      authorizationPassword: password.value,
      userAgentString: 'Bevatel-Web-Phone',
      transportOptions: {
        server: this.server,
        traceSip: true,
        reconnectAttempts: 10,
        reconnectInterval: 5000,
      },
      delegate: {
        onInvite: this.onInvite.bind(this),
      },
    };

    this.userAgent = new UserAgent(transportOptions);

    this.registerer = new Registerer(this.userAgent, {
      refreshFrequency: 50,
      extraHeaders: buildExtraHeaders(location),
    });
    this.registerer.stateChange.addListener((state: RegistererState) => {
      this._registrationStatus.value = state;
    });
    this.userAgent.start().then(async () => {
      if (this.registerer && sipOptions.connection === 'register' && !this.isCurrentServiceOffline) {
        await this.register();
      }
    });

    return await new Promise((resolve) => {
      this.userAgent?.transport.stateChange.addListener(async (state: TransportState) => {
        this._connectionStatus.value = state;

        if (state === TransportState.Connected) {
          const currentWorkspaceName = useUserStore().currentWorkspace?.name;
          if (currentWorkspaceName && workspacesRequiringSaLocation.includes(currentWorkspaceName)) {
            const { initUserCountry } = useUserIpDetails();
            await initUserCountry();
          }
          resolve(true);
        } else if (state === TransportState.Disconnected) {
          toast.error(trans('voip.sip_connection_status.failed_trans'));

          resolve(false);
        }
      });
    });
  }

  /**
   * Terminates all calls, un-registers, and disconnects from the SIP server.
   * Cleans up the UserAgent and Registerer instances.
   * @returns {Promise<void>}
   */
  public async disconnect(): Promise<void> {
    if (!this.userAgent) {
      return;
    }

    this.hangupAll();

    // The existing unregister method already checks if it needs to run.
    await this.unregister();

    this._connectionStatus.value = 'Disconnected';
    this.userAgent = undefined;
    this.registerer = undefined;
  }

  /**
   * Registers the UserAgent with the SIP server.
   * Will connect first if not already connected.
   * @param {boolean} force - If true, will force the registration even if the service is offline.
   *                        and will remove the service from offline list.
   * @returns {Promise<void>}
   */
  public async register(force: boolean = false): Promise<void> {
    if (force) {
      this.removeCurrentServiceFromOffline();
    }

    if (this.isCurrentServiceOffline) {
      return Promise.resolve();
    }

    if (this._connectionStatus.value !== 'Connected') {
      await this.connect();
    } else if (this.registerer && this.registerer?.state !== RegistererState.Registered) {
      await this.registerer.register();
      this.setNewPing();
    }
  }

  /**
   * Un-registers the UserAgent from the SIP server.
   * @param {boolean} setOffline - If true, marks current service as offline in local storage.
   * @returns {Promise<void>}
   */
  public async unregister(setOffline: boolean = false): Promise<void> {
    if (setOffline) {
      this.addCurrentServiceToOffline();
    }
    if (this.registerer && this.registerer?.state !== RegistererState.Unregistered) {
      await this.registerer.unregister();
    }
  }

  /**
   * Initiates a new outbound SIP call to the specified number.
   * This method performs several validation checks before starting the call,
   * such as verifying the user's connection status and active call count.
   *
   * @public
   * @async
   * @param {string | number} number - The destination phone number or SIP URI.
   * @returns {Promise<void>} A Promise that resolves once the call has been initiated or if it fails validation.
   */
  public async call(number: string | number): Promise<void> {
    const { error } = useToast();
    const sipStore = useSipStore();
    const currentWorkspaceName = useUserStore().currentWorkspace?.name;
    const isSaLocationRequired = currentWorkspaceName && workspacesRequiringSaLocation.includes(currentWorkspaceName);

    if (isSaLocationRequired) {
      const { userIpDetails } = useUserIpDetails();
      if (!userIpDetails.value.country_code || userIpDetails.value.country_code !== 'SA') {
        error(trans('voip.not_allowed_call_reason.country_not_allowed_trans'));
        return;
      }
    }

    if (sipStore.activeCall?.status && ['calling', 'ringing', 'Initial'].includes(sipStore.activeCall?.status)) {
      // prevent start a call before take action for current active call
      error(trans('voip.not_allowed_call_reason.active_call_need_action_trans'));
      return;
    }

    if (Object.keys(sipStore.currentCalls).length >= 2) {
      // prevent more than 2 calls
      error(trans('voip.not_allowed_call_reason.prevent_more_than_x_calls_trans', { count: 2 }));
      return;
    }

    if (!this.extension || !this.userAgent) {
      if (!(await this.connect())) {
        return;
      }

      if (!this.extension || !this.userAgent) {
        return;
      }
    }

    const { country_configs, fallback_country: defaultCountry } = useUserStore().normalizerConfig;
    const normalized = normalize(number, country_configs, defaultCountry);
    const asteriskNumber = normalized.asterisk;

    const encodedChars = encodeURIComponent(asteriskNumber);
    const aor = 'sip:' + encodedChars + '@' + this.url;
    const target = UserAgent.makeURI(aor);
    if (!target) {
      return;
    }

    const { callLogApi } = await import('@bv/api');

    // Ensure location is retrieved and cached
    const location = await this.ensureLocationCached();
    const extraHeaders = buildExtraHeaders(location);

    const inviterOptions: InviterOptions = {
      extraHeaders,
    };

    const inviter = new Inviter(this.userAgent, target, inviterOptions);
    const call = sipStore.addCall(inviter.id, inviter, 'outbound');

    call.cdr.direction = 'outbound';

    call.cdr.fromNumber = this.extension;
    call.cdr.toNumber = asteriskNumber;
    call.cdr.startTime = moment().format('YYYY-MM-DD HH:mm:ss');
    call.cdr.normalization = { ...normalized, number };

    const inviterInviteOptions: InviterInviteOptions = {
      requestDelegate: {
        onReject: (response) => {
          call.endCallReason = response?.message?.headers['X-Asterisk-Hangupcausecode']
            ? response?.message?.headers['X-Asterisk-Hangupcausecode'][0]?.raw
            : undefined;

          switch (call.endCallReason) {
            case '17':
              call.cdr.status = 'busy';
              break;

            case '20':
              call.cdr.status = 'unavailable';
              break;

            case '19':
              call.cdr.status = 'no_answer';
              break;

            case undefined:
              call.cdr.status = call.isCanceled ? 'canceled' : 'not_found';
              call.isCanceled = false;
              break;
          }

          call.endCallReason = call.cdr.status || undefined;

          callLogApi.create({ call });
        },
      },
    };

    call.session.invite(inviterInviteOptions);
    call.status = 'calling';

    if (sipStore.previousCall) {
      this.hold(sipStore.previousCall);
    }

    this.startRingAudioMedia(call, 'calling');

    call.session.stateChange.addListener(async (state: SessionState) => {
      call.status = state;

      switch (state) {
        case SessionState.Initial:
          break;
        case SessionState.Establishing:
          break;
        case SessionState.Established:
          call.isEstablished = true;
          this.resume(call);

          call.cdr.startTimeAnswer = moment().format('YYYY-MM-DD HH:mm:ss');
          call.cdr.status = 'answered';
          break;

        case SessionState.Terminating:
        case SessionState.Terminated:
          this.stopRemoteAudioMedia(call);
          call.cdr.endCallTime = moment().format('YYYY-MM-DD HH:mm:ss');

          if (call.isEstablished) {
            callLogApi.create({ call });
          }

          sipStore.removeCall(call);
          break;
      }

      if ([SessionState.Established, SessionState.Terminated, SessionState.Terminating].includes(state)) {
        this.stopRingingMedia(call);
      }
    });
  }

  /**
   * Accepts an incoming call invitation.
   * @param {Call} call - The incoming call to accept.
   * @returns {Promise<void>}
   */
  public async accept(call: Call): Promise<void> {
    // Ensure location is retrieved and cached
    const location = await this.ensureLocationCached();
    const options: InvitationAcceptOptions = {
      ...invitationAcceptOptions,
      extraHeaders: buildExtraHeaders(location),
    };
    (call.session as Invitation).accept(options);
  }

  /**
   * Handles a call pickup request, performing validation checks before invoking the callback.
   * @param {string} channel - The channel to be picked up.
   * @param {string} currentPresenceStatus - The agent's current presence status (e.g., 'available').
   * @param {(payload: { channel: string }) => void} callback - The function to call to perform the pickup.
   * @returns {void}
   */
  public pickup(
    channel: string,
    currentPresenceStatus: string,
    callback: (payload: { channel: string }) => void,
  ): void {
    const sipStore = useSipStore();
    const { error } = useToast();
    const currentUserCalls = Object.values(sipStore.currentCalls);

    // Check agent availability
    if (currentPresenceStatus !== 'available') {
      error(trans('voip.not_allowed_call_reason.not_available_trans'));
      return;
    }

    // Check maximum call limit (2 calls)
    if (currentUserCalls.length == 2) {
      error(trans('voip.not_allowed_call_reason.exceed_max_calls_trans'));
      return;
    }

    // Check if single active call is inbound
    if (sipStore.activeCall?.direction === 'inbound') {
      error(trans('voip.not_allowed_call_reason.has_inbound_active_call_trans'));
      return;
    }

    callback({ channel });
  }

  /**
   * Transfers an active call to another number.
   * @param {string} id - The ID of the call to transfer.
   * @param {string | number} number - The destination number for the transfer.
   * @returns {Promise<void>}
   */
  public async transfer(id: string, number: string | number): Promise<void> {
    const call = this.getCallByIdOrActiveCall(id);
    if (call) {
      const agent = call.session;

      const aor = `sip:${number}@${this.url}`;
      const uri = UserAgent.makeURI(aor);

      if (agent.sessionDescriptionHandler && uri) {
        return agent.refer(uri).then(() => {
          this.hangup(call);
        });
      }
    }
    return Promise.reject();
  }

  /**
   * Activates a specific call, resuming it and putting all other calls on hold.
   * @param {Call} toBeActiveCall - The call to be made active.
   * @returns {void}
   */
  public activateCall(toBeActiveCall: Call): void {
    const sipStore = useSipStore();

    if (toBeActiveCall.isActive) {
      return;
    }

    this.resume(toBeActiveCall);

    sipStore.activateCall(toBeActiveCall);
  }

  /**
   * Terminates a call. The method (reject, cancel, bye) depends on the call's current state.
   * @param {Call} call - The call to terminate.
   * @param {boolean} [activatePrevious=false] - If true, activates the previous call after hanging up.
   * @returns {void}
   */
  public hangup(call: Call, activatePrevious: boolean = false): void {
    switch (call.status) {
      case SessionState.Initial:
      case 'ringing':
        (call.session as Invitation).reject({ statusCode: 486 });
        break;

      case 'calling':
      case 'Establishing':
        (call.session as Inviter).cancel();
        call.isCanceled = true;
        break;

      case SessionState.Established:
        call.session.bye();
        break;
    }

    const sipStore = useSipStore();
    const previousCall = sipStore.previousCall;
    if (activatePrevious && previousCall?.id) {
      this.activateCall(previousCall);
    }
  }

  /**
   * Terminates all current calls.
   * @returns {void}
   */
  public hangupAll(): void {
    Object.values(useSipStore().currentCalls).forEach((call) => {
      this.hangup(call);
    });
  }

  /**
   * Sends DTMF tones for the active call.
   * @param {string} tones - The DTMF tones to send (e.g., '1', '2', '#').
   * @returns {void}
   */
  public sendDTMF(tones: string): void {
    const sipStore = useSipStore();

    const sdh = sipStore.activeCall?.session.sessionDescriptionHandler;

    if (sdh) {
      const options = {
        duration: 500,
        interToneGap: 500,
      };

      sdh.sendDtmf(tones, options);
      this.startToneAudioMedia(tones);
    }
  }

  /**
   * Mutes the local audio for a specific call.
   * @param {Call} call - The call to mute.
   * @returns {void}
   */
  public mute(call: Call): void {
    this.setMuteStatus(true, call);
  }

  /**
   * Un-mutes the local audio for a specific call.
   * @param {Call} call - The call to unmute.
   * @returns {void}
   */
  public unmute(call: Call): void {
    this.setMuteStatus(false, call);
  }

  /**
   * Puts a specific call on hold.
   * @param {Call} call - The call to put on hold.
   * @returns {void}
   */
  public hold(call: Call): void {
    this.setHoldStatus(true, call);
  }

  /**
   * Resumes a specific call from hold.
   * @param {Call} call - The call to resume.
   * @returns {void}
   */
  public resume(call: Call): void {
    this.setHoldStatus(false, call);
  }

  /**
   * @private
   * Sets the mute status for a call's local audio track.
   * @param {boolean} muteStatus - True to mute, false to unmute.
   * @param {Call} call - The target call.
   * @returns {void}
   */
  private setMuteStatus(muteStatus: boolean, call: Call): void {
    (call.session.sessionDescriptionHandler as SessionDescriptionHandler).peerConnection
      ?.getSenders()
      .forEach((sender) => {
        if (sender.track && sender.track.kind === 'audio') {
          sender.track.enabled = !muteStatus;
        }
      });
    call.isMute = muteStatus;
  }

  /**
   * @private
   * Sets the hold status for a call.
   * @param {boolean} holdStatus - True to hold, false to resume.
   * @param {Call} call - The target call.
   * @returns {void}
   */
  private setHoldStatus(holdStatus: boolean, call: Call): void {
    const session = call.session as Inviter & { isHeld: boolean };

    if (
      session.isHeld === holdStatus ||
      (call.status && ['Terminated', 'Terminating', 'Establishing'].includes(call.status))
    ) {
      return;
    }

    call.isHold = holdStatus;

    if (!holdStatus) {
      Object.values(useSipStore().currentCalls).forEach((c) => {
        if (c.id !== call.id) {
          this.hold(c);
        }
      });

      this.setupRemoteAudioMedia(call);
    } else {
      this.stopRemoteAudioMedia(call);
    }

    try {
      if (holdStatus) {
        session.invite({
          sessionDescriptionHandlerModifiers: [Web.holdModifier],
        });
      } else {
        session.invite({
          sessionDescriptionHandlerModifiers: [],
        });
      }
    } catch {
      //
    }
  }

  /**
   * @private
   * Retrieves a call by its ID, or returns the currently active call if no ID is provided.
   * @param {string} [id] - The optional ID of the call to retrieve.
   * @returns {Call | undefined} The found call or the active call.
   */
  private getCallByIdOrActiveCall(id?: string): Call | undefined {
    if (id) {
      return useSipStore().getCall(id);
    }
    return useSipStore().activeCall;
  }

  /**
   * Handles an incoming SIP call invitation, performing checks and managing the call lifecycle.
   * It will reject the call if the number of active calls exceeds the limit.
   *
   * @private
   * @async
   * @param {Invitation} invitation - The incoming SIP.js invitation object.
   * @returns {Promise<void>} A Promise that resolves once the invitation has been processed.
   */

  private async onInvite(invitation: Invitation): Promise<void> {
    const sipStore = useSipStore();

    const name = invitation.assertedIdentity?.displayName;
    const number = invitation.remoteIdentity.uri.user;

    if (Object.keys(sipStore.currentCalls).length >= 2) {
      // prevent more than 2 calls
      const { warning } = useToast();
      invitation.reject();
      warning(
        trans('voip.not_allowed_call_reason.x_tried_to_reach_you_trans', {
          name: `${name + `${number !== name ? ` (${number})` : ''}`}`,
        }),
      );
      return;
    }

    const call = sipStore.addCall(invitation.id, invitation, 'inbound');
    const title = `Call From: ${name + `${number !== name ? ` (${number})` : ''}`}`;
    notification({ title, body: 'click here to show the call', allowFocusedTab: true });

    this.startRingAudioMedia(call, 'ringing');
    call.status = 'ringing';
    call.cdr.direction = 'inbound';

    const { callLogApi } = await import('@bv/api');

    call.session.stateChange.addListener(async (state: SessionState) => {
      call.status = state;
      call.cdr.startTime = moment().format('YYYY-MM-DD HH:mm:ss');

      switch (state) {
        case SessionState.Established:
          this.setupRemoteAudioMedia(call);
          call.isEstablished = true;
          call.isAnswered = true;
          call.cdr.startTimeAnswer = moment().format('YYYY-MM-DD HH:mm:ss');
          call.cdr.status = 'answered';
          break;

        case SessionState.Terminating:
        case SessionState.Terminated:
          call.cdr.endCallTime = moment().format('YYYY-MM-DD HH:mm:ss');

          if (!call.isAnswered) {
            call.cdr.status = call.direction === 'inbound' ? 'missed' : 'canceled';
          }

          this.stopRemoteAudioMedia(call);
          sipStore.removeCall(call);

          callLogApi.create({ call });

          break;
      }

      if ([SessionState.Established, SessionState.Terminated, SessionState.Terminating].includes(state)) {
        this.stopRingingMedia(call);
      }
    });
  }

  /**
   * @private
   * Sets up the remote audio stream for a call once it's established.
   * @param {Call} call - The call instance.
   * @returns {void}
   */
  private setupRemoteAudioMedia(call: Call): void {
    const remoteStream = new MediaStream();

    const sdh = call.session.sessionDescriptionHandler as SessionDescriptionHandler;

    sdh.peerConnection?.getReceivers().forEach((receiver) => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    this.startRemoteAudioMedia(call, remoteStream);

    this.setupAudioVisualizer(call);
  }

  /**
   * @private
   * Attaches the remote audio stream to the call object for use with the audio visualizer.
   * @param {Call} call - The call instance.
   * @returns {void}
   */
  private setupAudioVisualizer(call: Call): void {
    const remoteStream = new MediaStream();
    const sdh = call.session.sessionDescriptionHandler as SessionDescriptionHandler;

    sdh.peerConnection?.getReceivers().forEach((receiver) => {
      if (receiver.track && receiver.track.kind === 'audio') {
        remoteStream.addTrack(receiver.track);
      }
    });

    call.audioStream = remoteStream;
  }

  /**
   * @private
   * Stops and cleans up the remote audio element for a call.
   * @param {Call} call - The call instance.
   * @returns {void}
   */
  private stopRemoteAudioMedia(call: Call): void {
    if (call.remoteAudioElement) {
      call.remoteAudioElement.pause();
      call.remoteAudioElement.srcObject = null;
      call.remoteAudioElement.remove();
      call.remoteAudioElement = undefined;
    }
  }

  /**
   * @private
   * Creates and plays a looping ringtone or calling tone audio element.
   * @param {Call} call - The call instance.
   * @param {'calling' | 'ringing'} type - The type of tone to play.
   * @returns {void}
   */
  private startRingAudioMedia(call: Call, type: 'calling' | 'ringing'): void {
    call.ringAudioElement = document.createElement('audio');
    call.ringAudioElement.loop = true;
    call.ringAudioElement.src = type === 'ringing' ? ringtone : phoneCall;
    call.ringAudioElement.play();
  }

  /**
   * @private
   * Plays a one-off DTMF tone locally.
   * @param {string} tone - The DTMF character tone to play.
   * @returns {void}
   */
  private startToneAudioMedia(tone: string): void {
    const src = tones[tone];

    if (src) {
      const newAudioElement = document.createElement('audio');
      newAudioElement.src = tones[tone];
      newAudioElement.play();
      newAudioElement.addEventListener('ended', () => {
        newAudioElement.remove();
      });
    }
  }

  /**
   * @private
   * Stops and cleans up the ringing audio element.
   * @param {Call} call - The call instance.
   * @returns {void}
   */
  private stopRingingMedia(call: Call): void {
    if (call.ringAudioElement) {
      call.ringAudioElement.pause();
      call.ringAudioElement.remove();
      call.ringAudioElement = undefined;
    }
  }

  /**
   * @private
   * Sends a 'ping' message to keep the WebSocket connection alive and schedules the next ping.
   * @returns {void}
   */
  private setNewPing(): void {
    if (this.userAgent?.transport.isConnected()) {
      try {
        this.userAgent.transport.send('ping');
      } catch (error) {
        throw error as Error;
      }

      // Schedule the next ping, send ping every 20 seconds
      setTimeout(() => this.setNewPing(), 20_000);
    }
  }
}

/**
 * A singleton instance of the Sip class for global access.
 */
export const sip = new Sip();

/**
 * @exports sip
 * The default export is the singleton Sip instance.
 */
export default sip;
